import { ContentFormats } from '@emerald-works-nova/components'
import {
  ArrowSquareOut,
  BookmarksSimple,
  BookOpen,
  BookOpenText,
  ChartLineUp,
  Crown,
  FileArrowDown,
  FileText,
  GraduationCap,
  Lightning,
  MusicNote,
  PlayCircle,
  Question
} from '@phosphor-icons/react'
import React from 'react'

// MT.3 brand refresh — content-type icons come from Phosphor (regular weight)
// so the visual language matches the rest of the refreshed cards. The shared
// `@emerald-works-nova/components.ContentFormats` package still owns the
// labels/types map (and ships Material icons we ignore here); we override
// the icon per format/type below.
const PHOSPHOR_PROPS = { size: 18, weight: 'regular' }

const ICON_BY_FORMAT = {
  // `data-testid="DescriptionIcon"` keeps the e2e contract that
  // `assertFilteredSearchResults` relies on — that selector was auto-emitted
  // by MUI's `<Description />` icon before the Phosphor migration. Phosphor
  // forwards arbitrary props to the underlying SVG, so the testid lands on
  // the rendered element.
  article: <FileText {...PHOSPHOR_PROPS} data-testid='DescriptionIcon' />,
  audio: <MusicNote {...PHOSPHOR_PROPS} />,
  infographic: <ChartLineUp {...PHOSPHOR_PROPS} />,
  video: <PlayCircle {...PHOSPHOR_PROPS} />,
  quiz: <Question {...PHOSPHOR_PROPS} />,
  skillbooks: <BookOpen {...PHOSPHOR_PROPS} />,
  bespoke: <Crown {...PHOSPHOR_PROPS} />,
  elearning: <GraduationCap {...PHOSPHOR_PROPS} />,
  interactive: <FileArrowDown {...PHOSPHOR_PROPS} />,
  playlist: <BookmarksSimple {...PHOSPHOR_PROPS} />,
  external: <ArrowSquareOut {...PHOSPHOR_PROPS} />,
  'skill-bite': <Lightning {...PHOSPHOR_PROPS} />
}

const ICON_BY_TYPE = {
  msb: <BookOpenText {...PHOSPHOR_PROPS} />
}

const useContentType = ({ primaryType }) => {
  const { formatIcon, formatLabel, typeLabel } = React.useMemo(() => {
    const [formatName, typeName] = primaryType || []

    if (!formatName) {
      return {
        formatLabel: 'Content',
        formatIcon: ICON_BY_FORMAT.article
      }
    }

    if (formatName === 'playlist') {
      return {
        formatLabel: 'Playlist',
        formatIcon: ICON_BY_FORMAT.playlist
      }
    }

    if (formatName === 'external') {
      return {
        formatLabel: 'External Link',
        formatIcon: ICON_BY_FORMAT.external
      }
    }

    if (formatName === 'skill-bite') {
      return {
        formatLabel: 'Skill Bite',
        formatIcon: ICON_BY_FORMAT['skill-bite']
      }
    }

    if (typeName === 'msb') {
      return {
        formatLabel: 'Manager Skill Builder',
        formatIcon: ICON_BY_TYPE.msb
      }
    }

    const format = ContentFormats.find(f => f.name === formatName)
    const metadata = {
      formatLabel: format?.label,
      // Prefer the Phosphor icon when we have a local mapping; fall back to
      // whatever icon the shared ContentFormats package ships so unknown
      // formats still render something.
      formatIcon: ICON_BY_FORMAT[formatName] ?? format?.icon
    }
    if (typeName) {
      const type = format?.types.find(t => t.name === typeName)
      metadata.typeLabel = type?.label
    }
    return metadata
  }, [primaryType])

  return {
    formatIcon,
    formatLabel,
    typeLabel,
    label: typeLabel || formatLabel || 'Content'
  }
}

export default useContentType
